"use client";
import FuseScrollbars from "@fuse/core/FuseScrollbars";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { memo, ReactNode, RefObject, useImperativeHandle, useRef } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";
import { SystemStyleObject, Theme } from "@mui/system";
import FusePageSimpleHeader from "./FusePageSimpleHeader";
import FusePageSimpleSidebar, { FusePageSimpleSidebarProps } from "./FusePageSimpleSidebar";
import { FuseScrollbarsProps } from "../FuseScrollbars/FuseScrollbars";

const headerHeight = 120;
const toolbarHeight = 64;

/**
 * Props for the FusePageSimple component.
 */
type FusePageSimpleProps = SystemStyleObject<Theme> & {
	className?: string;
	header?: ReactNode;
	content?: ReactNode;
	/** Aplica o espaçamento padrão do conteúdo. Desative apenas para layouts full-bleed. */
	contentSpacing?: boolean;
	scroll?: "normal" | "page" | "content";
	leftSidebarProps?: FusePageSimpleSidebarProps;
	rightSidebarProps?: FusePageSimpleSidebarProps;
	contentScrollbarsProps?: FuseScrollbarsProps;
	ref?: RefObject<{
		toggleLeftSidebar: (val: boolean) => void;
		toggleRightSidebar: (val: boolean) => void;
	}>;
};

/**
 * The Root styled component is the top-level container for the FusePageSimple component.
 */
const Root = styled("div")<FusePageSimpleProps>(({ theme, ...props }) => ({
	display: "flex",
	flexDirection: "column",
	minWidth: 0,
	minHeight: "100%",
	position: "relative",
	flex: "1 1 auto",
	width: "100%",
	height: "auto",
	backgroundColor: theme.vars.palette.background.default,

	"&.FusePageSimple-scroll-content": {
		height: "100%",
		...(props.contentSpacing && {
			minHeight: 0,
			height: "calc(100dvh - 76px)",
			[theme.breakpoints.up("md")]: { height: "calc(100dvh - 84px)" },
		}),
	},

	"& .FusePageSimple-wrapper": {
		display: "flex",
		flexDirection: "row",
		flex: "1 1 auto",
		zIndex: 2,
		minWidth: 0,
		height: "100%",
		backgroundColor: theme.vars.palette.background.default,

		...(props.scroll === "content" && {
			position: "absolute",
			top: 0,
			bottom: 0,
			right: 0,
			left: 0,
			overflow: "hidden",
		}),
	},

	"& .FusePageSimple-header": {
		display: "flex",
		flex: "0 0 auto",
		backgroundColor: theme.vars.palette.background.paper,
		backgroundSize: "cover",
		borderBottomWidth: 1,
		borderStyle: "solid",
		borderColor: theme.vars.palette.divider,
		// Em scroll="page" o toolbar é forçado a sticky top:0; fixa o header logo abaixo dele
		...(props.scroll === "page" && {
			position: "sticky",
			top: 48, // altura do toolbar (mobile, min-h-12)
			zIndex: 11, // acima do conteúdo que rola; o toolbar (z-20 no contexto pai) ainda fica por cima
			"@media (min-width:768px)": {
				top: 56, // altura do toolbar (md+, md:min-h-14) — Tailwind md=768px
			},
		}),
	},

	"& .FusePageSimple-topBg": {
		position: "absolute",
		left: 0,
		right: 0,
		top: 0,
		height: headerHeight,
		pointerEvents: "none",
	},

	"& .FusePageSimple-contentWrapper": {
		display: "flex",
		flexDirection: "column",
		width: "100%",
		flex: "1",
		zIndex: 9999,
		...(props.scroll === "content" && {
			minHeight: 0,
			overflow: "hidden",
		}),
	},

	"& .FusePageSimple-toolbar": {
		height: toolbarHeight,
		minHeight: toolbarHeight,
		display: "flex",
		alignItems: "center",
	},

	"& .FusePageSimple-content": {
		display: "flex",
		flexDirection: "column",
		flex: "1 1 auto",
		alignItems: "start",
		minHeight: 0,
		...(props.scroll === "content" && {
			overflowY: "auto",
		}),
		"& > .container": {
			display: "flex",
			flexDirection: "column",
			minHeight: "100%",
			...(props.scroll === "content" &&
				props.contentSpacing && {
					position: "relative",
					height: "100%",
					minHeight: 0,
				}),
		},
	},

	"& .FusePageSimple-sidebarWrapper": {
		overflow: "hidden",
		backgroundColor: "transparent",
		position: "absolute",
		"&.permanent": {
			[theme.breakpoints.up("lg")]: {
				position: "relative",
				marginLeft: 0,
				marginRight: 0,
				transition: theme.transitions.create("margin", {
					easing: theme.transitions.easing.sharp,
					duration: theme.transitions.duration.leavingScreen,
				}),
				"&.closed": {
					transition: theme.transitions.create("margin", {
						easing: theme.transitions.easing.easeOut,
						duration: theme.transitions.duration.enteringScreen,
					}),

					"&.FusePageSimple-leftSidebar": {
						marginLeft: -props.leftSidebarProps?.width,
					},
					"&.FusePageSimple-rightSidebar": {
						marginRight: -props.rightSidebarProps?.width,
					},
				},
			},
		},
	},

	"& .FusePageSimple-sidebar": {
		position: "absolute",
		backgroundColor: theme.vars.palette.background.paper,
		color: theme.vars.palette.text.primary,

		"&.permanent": {
			[theme.breakpoints.up("lg")]: {
				position: "relative",
			},
		},
		maxWidth: "100%",
		height: "100%",
	},

	"& .FusePageSimple-leftSidebar": {
		width: props.leftSidebarProps?.width,
		maxWidth: props.leftSidebarProps?.width,

		[theme.breakpoints.up("lg")]: {
			borderRight: `1px solid ${theme.vars.palette.divider}`,
			borderLeft: 0,
		},
	},

	"& .FusePageSimple-rightSidebar": {
		width: props.rightSidebarProps?.width,
		maxWidth: props.rightSidebarProps?.width,

		[theme.breakpoints.up("lg")]: {
			borderLeft: `1px solid ${theme.vars.palette.divider}`,
			borderRight: 0,
			flex: "1",
		},
	},

	"& .FusePageSimple-backdrop": {
		position: "absolute",
	},
}));

const sidebarPropsDefaults = { variant: "permanent" as const };

/**
 * The FusePageSimple component is a layout component that provides a simple page layout with a header, left sidebar, right sidebar, and content area.
 * It is designed to be used as a top-level component for an application or as a sub-component within a larger layout.
 */
function FusePageSimple(props: FusePageSimpleProps) {
	const {
		scroll = "page",
		className,
		header,
		content,
		contentSpacing = true,
		leftSidebarProps,
		rightSidebarProps,
		contentScrollbarsProps,
		ref,
	} = props;

	const leftSidebarRef = useRef<{ toggleSidebar: (T: boolean) => void }>(null);
	const rightSidebarRef = useRef<{ toggleSidebar: (T: boolean) => void }>(null);
	const rootRef = useRef(null);

	useImperativeHandle(ref, () => ({
		rootRef,
		toggleLeftSidebar: (val: boolean) => {
			leftSidebarRef?.current?.toggleSidebar(val);
		},
		toggleRightSidebar: (val: boolean) => {
			rightSidebarRef?.current?.toggleSidebar(val);
		},
	}));

	return (
		<>
			<GlobalStyles
				styles={() => ({
					...(scroll !== "page" && {
						"#fuse-toolbar": {
							position: "static!important",
						},
						"#fuse-footer": {
							position: "static!important",
						},
					}),
					...(scroll === "page" && {
						"#fuse-toolbar": {
							position: "sticky",
							top: 0,
						},
					}),
				})}
			/>
			<Root
				className={clsx("FusePageSimple-root", `FusePageSimple-scroll-${scroll}`, className)}
				ref={rootRef}
				scroll={scroll}
				contentSpacing={contentSpacing}
				leftSidebarProps={{
					...sidebarPropsDefaults,
					...leftSidebarProps,
				}}
				rightSidebarProps={{
					...sidebarPropsDefaults,
					...rightSidebarProps,
				}}
			>
				<div className="z-10 flex h-full flex-auto flex-col">
					<div className="FusePageSimple-wrapper">
						<FusePageSimpleSidebar
							position="left"
							ref={leftSidebarRef}
							{...sidebarPropsDefaults}
							{...leftSidebarProps}
						/>
						<div className="FusePageSimple-contentWrapper">
							{header && <FusePageSimpleHeader header={header} />}

							{content && (
								<FuseScrollbars
									enable={scroll === "content"}
									className={clsx("FusePageSimple-content")}
									scrollToTopOnRouteChange
									{...contentScrollbarsProps}
								>
									<div
										className={clsx(
											"container",
											contentSpacing && "flex min-w-0 flex-col",
											contentSpacing &&
												(scroll === "content" ? "gap-2 px-4 py-3 md:px-5" : "gap-4 p-4 sm:p-8"),
											contentSpacing && header && scroll !== "content" && "mt-4 sm:mt-0",
										)}
									>
										{content}
									</div>
								</FuseScrollbars>
							)}
						</div>
						<FusePageSimpleSidebar
							position="right"
							ref={rightSidebarRef}
							{...sidebarPropsDefaults}
							{...rightSidebarProps}
						/>
					</div>
				</div>
			</Root>
		</>
	);
}

const StyledFusePageSimple = memo(styled(FusePageSimple)``);

export default StyledFusePageSimple;
